import { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  useConnectors,
  useCreateSonarQubeConnection,
  useDisableSonarQubeConnection,
  useDeleteConnectorInstance,
  useDisconnectConnector,
  useSaveToken,
  useSonarQubeConnection,
  useTestSonarQubeConnection,
  useTitanEndpointStatus,
  useUpdateSonarQubeConnection,
} from '@/hooks'
import { useAuth } from '@/hooks/useAuth'
import {
  ConnectorCatalog,
  ConnectorDetailView,
  DatabaseConnectorsSection,
  SonarQubeConnectorPanel,
  SonarQubeDisableConfirmDialog,
  SonarQubeEnableDialog,
  TokenDialog,
  CONNECTOR_LABELS,
  supportsMultipleInstances,
} from '@/components/projects/connectors'
import { ProjectSectionHeader } from '@/components/projects'
import type { Connector, ConnectorType, SaveSonarQubeConnectionRequest, TestSonarQubeConnectionRequest } from '@/types'
import { Permissions } from '@/lib/permissions'
import { getErrorCode, getErrorMessage } from '@/api/errors'
import { extractValidationMessage, hasValidationError } from '@/lib/validationErrors'
import { describeTokenSaveError } from '@/lib/connectorTokenErrors'

export function ConnectorsPage() {
  const { projectId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { hasPermission } = useAuth(projectId)
  const canManage = hasPermission(Permissions.Project.Connectors.Manage, projectId)
  const { data: connectors = [], isLoading: isLoadingConnectors } = useConnectors(projectId!)
  const disconnectConnector = useDisconnectConnector(projectId!)
  const deleteConnectorInstance = useDeleteConnectorInstance(projectId!)
  const saveToken = useSaveToken(projectId!)
  const { data: titanEndpointStatus } = useTitanEndpointStatus(projectId!)

  const { data: sonarQubeConnection, isLoading: isLoadingSonarQubeConnection } = useSonarQubeConnection(projectId!)
  const createSonarQubeConnection = useCreateSonarQubeConnection(projectId!)
  const updateSonarQubeConnection = useUpdateSonarQubeConnection(projectId!)
  const disableSonarQubeConnection = useDisableSonarQubeConnection(projectId!)
  const testSonarQubeConnection = useTestSonarQubeConnection(projectId!)

  const [selectedType, setSelectedType] = useState<ConnectorType | null>(null)
  const [disconnectingType, setDisconnectingType] = useState<ConnectorType | null>(null)
  const [tokenDialogType, setTokenDialogType] = useState<ConnectorType | null>(null)
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false)
  const [reconnectConnector, setReconnectConnector] = useState<Connector | null>(null)
  const [tokenInlineError, setTokenInlineError] = useState<string | null>(null)
  const [tokenConnectionNameError, setTokenConnectionNameError] = useState<string | null>(null)
  const [sonarQubePanelOpen, setSonarQubePanelOpen] = useState(false)
  const [sonarQubeEnableDialogOpen, setSonarQubeEnableDialogOpen] = useState(false)
  const [sonarQubeDisableConfirmOpen, setSonarQubeDisableConfirmOpen] = useState(false)
  const [sonarQubeTokenInlineError, setSonarQubeTokenInlineError] = useState<string | null>(null)
  const [sonarQubeSaveError, setSonarQubeSaveError] = useState<string | null>(null)

  useEffect(() => {
    setTokenInlineError(null)
    setTokenConnectionNameError(null)
  }, [tokenDialogType])

  const reconnectId = searchParams.get('reconnect')

  useEffect(() => {
    if (!reconnectId || isLoadingConnectors || !canManage) return

    const target = connectors.find(c => c.id === reconnectId)
    if (target) {
      setReconnectConnector(target)
      setTokenDialogType(target.type)
      setTokenDialogOpen(true)
    }
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.delete('reconnect')
      return next
    }, { replace: true })
  }, [reconnectId, isLoadingConnectors, canManage, connectors, setSearchParams])

  const handleConnect = (type: ConnectorType) => {
    setTokenDialogType(type)
    setTokenDialogOpen(true)
  }

  const handleReconnectConnection = (connection: Connector) => {
    setReconnectConnector(connection)
    setTokenDialogType(connection.type)
    setTokenDialogOpen(true)
  }

  const handleCardClick = (type: ConnectorType) => {
    if (type === 'SonarQube') {
      setSonarQubePanelOpen(true)
    } else {
      setSelectedType(type)
    }
  }

  const handleCreateSonarQubeConnection = async (data: SaveSonarQubeConnectionRequest) => {
    setSonarQubeTokenInlineError(null)
    setSonarQubeSaveError(null)
    try {
      await createSonarQubeConnection.mutateAsync(data)
      toast.success('SonarQube connected successfully')
      setSonarQubeEnableDialogOpen(false)
    } catch (error) {
      if (getErrorCode(error) === 'CONNECTOR_UNAUTHORIZED') {
        setSonarQubeTokenInlineError(getErrorMessage(error) ?? 'Invalid token — check your SonarQube token.')
      } else {
        setSonarQubeSaveError(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to create SonarQube connection')
      }
    }
  }

  const handleUpdateSonarQubeConnection = async (data: SaveSonarQubeConnectionRequest) => {
    setSonarQubeTokenInlineError(null)
    setSonarQubeSaveError(null)
    try {
      await updateSonarQubeConnection.mutateAsync(data)
      toast.success('SonarQube connection updated')
    } catch (error) {
      if (getErrorCode(error) === 'CONNECTOR_UNAUTHORIZED') {
        setSonarQubeTokenInlineError(getErrorMessage(error) ?? 'Invalid token — check your SonarQube token.')
      } else {
        setSonarQubeSaveError(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to update SonarQube connection')
      }
    }
  }

  const handleTestSonarQubeConnection = async (data: TestSonarQubeConnectionRequest) => {
    await testSonarQubeConnection.mutateAsync(data)
  }

  const handleDisableSonarQubeConnection = async () => {
    try {
      await disableSonarQubeConnection.mutateAsync()
      toast.success('SonarQube disconnected successfully')
      setSonarQubeDisableConfirmOpen(false)
      setSonarQubePanelOpen(false)
    } catch {
      toast.error('Failed to disable SonarQube')
    }
  }

  const handleSaveToken = async (connectionName: string, token: string, email?: string, site?: string) => {
    if (!tokenDialogType) return

    setTokenInlineError(null)
    setTokenConnectionNameError(null)
    try {
      await saveToken.mutateAsync({
        connectorType: tokenDialogType,
        data: {
          connectorId: reconnectConnector?.id,
          connectionName: reconnectConnector?.connectionName ?? connectionName,
          token,
          email,
          site,
        },
      })
      if (reconnectConnector) {
        toast.success(`${reconnectConnector.connectionName || CONNECTOR_LABELS[tokenDialogType]} reconnected successfully`)
      } else {
        toast.success(
          supportsMultipleInstances(tokenDialogType)
            ? `${CONNECTOR_LABELS[tokenDialogType]} connection added successfully`
            : `${CONNECTOR_LABELS[tokenDialogType]} connected successfully`,
        )
      }
      setTokenDialogOpen(false)
      setTokenDialogType(null)
      setReconnectConnector(null)
    } catch (error) {
      const message = describeTokenSaveError(error)

      if (hasValidationError(error, 'ConnectionName')) {
        setTokenConnectionNameError(extractValidationMessage(error))
      } else if (message) {
        setTokenInlineError(message)
      } else {
        toast.error(`Failed to connect ${CONNECTOR_LABELS[tokenDialogType]}`)
      }
    }
  }

  const handleDisconnectConnection = async (connection: Connector): Promise<void> => {
    try {
      await deleteConnectorInstance.mutateAsync({ connectorType: connection.type, connectorId: connection.id })
      toast.success(`${connection.connectionName} disconnected successfully`)
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? `Failed to disconnect ${connection.connectionName}`)
    }
  }

  const handleDisconnect = async (type: ConnectorType) => {
    setDisconnectingType(type)
    try {
      await disconnectConnector.mutateAsync(type)
      toast.success(`${CONNECTOR_LABELS[type]} disconnected successfully`)
      setSelectedType(null)
    } catch {
      toast.error(`Failed to disconnect ${CONNECTOR_LABELS[type]}`)
    } finally {
      setDisconnectingType(null)
    }
  }

  if (isLoadingConnectors || isLoadingSonarQubeConnection) {
    return <div className="text-muted-foreground" role="status" aria-live="polite" data-testid="connectors-loading">Loading connectors...</div>
  }

  return (
    <div className="space-y-6">
      <ProjectSectionHeader sectionName="Connectors" />

      <ConnectorCatalog
        connectors={connectors}
        onCardClick={handleCardClick}
        onAddClick={handleConnect}
        canManage={canManage}
        sonarQubeEnabled={!!sonarQubeConnection}
        unavailableReasons={titanEndpointStatus?.configured === false ? { QubizTitan: 'Not set up for your organization' } : undefined}
      />

      <DatabaseConnectorsSection
        projectId={projectId!}
        connectors={connectors}
        canManage={canManage}
      />

      <ConnectorDetailView
        connectorType={selectedType}
        projectId={projectId!}
        connections={selectedType ? connectors.filter((c) => c.type === selectedType) : []}
        open={selectedType !== null}
        onOpenChange={(open) => { if (!open) setSelectedType(null) }}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        onAddConnection={handleConnect}
        onDisconnectConnection={handleDisconnectConnection}
        onReconnectConnection={handleReconnectConnection}
        isDisconnecting={disconnectingType === selectedType}
        isDisconnectingConnection={deleteConnectorInstance.isPending}
        canManage={canManage}
      />

      <TokenDialog
        projectId={projectId!}
        connectorType={tokenDialogType}
        open={tokenDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setTokenInlineError(null)
            setTokenConnectionNameError(null)
            setReconnectConnector(null)
          }
          setTokenDialogOpen(open)
        }}
        onSave={handleSaveToken}
        isSaving={saveToken.isPending}
        inlineError={tokenInlineError}
        connectionNameError={tokenConnectionNameError}
        reconnectConnectionName={reconnectConnector?.connectionName}
      />

      <SonarQubeConnectorPanel
        connection={sonarQubeConnection ?? undefined}
        open={sonarQubePanelOpen}
        onOpenChange={(open) => {
          if (!open) {
            setSonarQubeTokenInlineError(null)
            setSonarQubeSaveError(null)
          }
          setSonarQubePanelOpen(open)
        }}
        onTest={handleTestSonarQubeConnection}
        onSave={handleUpdateSonarQubeConnection}
        onDisableRequested={() => setSonarQubeDisableConfirmOpen(true)}
        onEnableRequested={() => setSonarQubeEnableDialogOpen(true)}
        isTesting={testSonarQubeConnection.isPending}
        isSaving={updateSonarQubeConnection.isPending}
        canManage={canManage}
        tokenInlineError={sonarQubeTokenInlineError}
        saveError={sonarQubeSaveError}
      />

      <SonarQubeEnableDialog
        open={sonarQubeEnableDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setSonarQubeTokenInlineError(null)
            setSonarQubeSaveError(null)
          }
          setSonarQubeEnableDialogOpen(open)
        }}
        onTest={handleTestSonarQubeConnection}
        onSave={handleCreateSonarQubeConnection}
        isTesting={testSonarQubeConnection.isPending}
        isSaving={createSonarQubeConnection.isPending}
        tokenInlineError={sonarQubeTokenInlineError}
        saveError={sonarQubeSaveError}
      />

      <SonarQubeDisableConfirmDialog
        connection={sonarQubeConnection ?? undefined}
        open={sonarQubeDisableConfirmOpen}
        onOpenChange={setSonarQubeDisableConfirmOpen}
        onConfirm={handleDisableSonarQubeConnection}
        isDisabling={disableSonarQubeConnection.isPending}
      />
    </div>
  )
}
